import React, {useEffect, useState} from "react";
import {
  createStyles,
  FormHelperText,
  Grid, makeStyles, Theme
} from "@material-ui/core";
import { useDispatch } from "react-redux";
import {
  CheckboxWithLabel,
  DropdownWithLabel,
  FileWithLabel,
  NumberFieldWithLabel,
  SimpleDialog,
  memoWithOpen,
} from "../../components";
import {addAssetFolder} from "../../utilities";
import {useTaskQueue} from "../../hooks";
import {deleteAsset} from "../../redux/assets-slice";
import {lasFileFilter} from "../../file-extensions";
import {tempDirectoryPath} from "../../electron-modules";
import { useTranslation } from "react-i18next";

enum ThinTechniques {
  Highest = "highest",
  Lowest = "lowest",
  Average = "average"
};

enum ThinDimensions {
  Two = 2,
  Three = 3,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    invalidThinningError: {
      padding: theme.spacing(1.0),
      color: theme.palette.warning.main,
      textAlign: "center"
    }
  }),
);

export const ThinLas = memoWithOpen((props) => {
  const {assets, open, onClose} = props;

  const dispatch = useDispatch();
  const {addRunningTask} = useTaskQueue();
  const classes = useStyles();
  const {t} = useTranslation();

  const defaultThinningOptions = {
    value: 0.1,
    error: false
  };

  const [outputPath, setOutputPath] = useState(null);
  const [thinTechnique, setThinTechnique] = useState(ThinTechniques.Lowest);
  const [thinDimension, setThinDimension] = useState(ThinDimensions.Two);
  const [thinning, setThinning] = useState(defaultThinningOptions);
  const [importResult, setImportResult] = useState(true);

  const inValidThinningValue = thinning.value <= 0;
  const canSubmit = (outputPath !== null && !thinning.error && thinDimension && thinTechnique && !inValidThinningValue);


  // TODO: actually handle multiple inputs (sent multiple exe commands to processing queue)
  const asset = assets ? assets[0] : undefined;

  const handleSubmit = () => {
    const pythonName = "thin";

    let commands = [
      "-p", pythonName,
      "--inputlas", asset.path,
      "--outputlas", outputPath,
      "--precision", thinning.value,
      "--keepvalue", thinTechnique,
      "--temp", tempDirectoryPath,
      "--dim", thinDimension
    ];

    const onFinish = async () => {
      if (!importResult) return;

      // Remove old LAS
      dispatch(deleteAsset(asset.id));

      // Add new thinned LAS
      await addAssetFolder(dispatch, [outputPath], asset.folderID);
    };

    onClose();
    addRunningTask(commands, onFinish);
  };

  const resetDefaults = () => {
    setOutputPath(null);
    setThinTechnique(ThinTechniques.Lowest);
    setThinDimension(ThinDimensions.Two);
    setThinning(defaultThinningOptions);
    setImportResult(true);
  };

  useEffect(() => {
    if (open) return;
    resetDefaults();
  }, [open]);

  return (
    <React.Fragment>
      <SimpleDialog
        open={open}
        onClose={onClose}
        onSubmit={handleSubmit}
        canSubmit={canSubmit}
        title={t('functions.thin_las.title')}
        button={t('buttons.submit')}
      >
        <Grid container spacing={2}>

          {/** Output thinning */}
          <Grid item xs={6}>
            <NumberFieldWithLabel
              title={t('functions.thin_las.thinning_value')}
              label={t('functions.thin_las.thinning_value_description')}
              value={thinning}
              min={0}
              max={100}
              step={0.05}
              setData={setThinning}
              data={thinning}
            />
          </Grid>

          {/** Thin Technique */}
          <Grid item xs={6}>
            <DropdownWithLabel
              title={t('functions.thin_las.thin_technique.name')}
              label={t('functions.thin_las.thin_technique.label')}
              value={thinTechnique}
              onChange={setThinTechnique}
              options={[
                [ThinTechniques.Average, t('functions.thin_las.thin_technique.average')],
                [ThinTechniques.Highest, t('functions.thin_las.thin_technique.highest')],
                [ThinTechniques.Lowest, t('functions.thin_las.thin_technique.lowest')],
              ]}
            />
          </Grid>

          {/** Thin Dimension */}
          <Grid item xs={6}>
            <DropdownWithLabel
              title={t('functions.thin_las.thin_dimension.name')}
              label={t('functions.thin_las.thin_dimension.label')}
              value={thinDimension}
              onChange={setThinDimension}
              options={[
                [ThinDimensions.Two, t('functions.thin_las.thin_dimension.thin_two_d')],
                [ThinDimensions.Three, t('functions.thin_las.thin_dimension.thin_three_d')],
              ]}
            />
          </Grid>

          {/** Import results */}
          <Grid item xs={6}>
            <CheckboxWithLabel
              title={t('functions.import_results')}
              label={t('functions.thin_las.thinned_is_imported')}
              value={importResult}
              onChange={setImportResult}
            />
          </Grid>

          {/** Output LAS */}
          <Grid item xs={6}>
            <FileWithLabel
              title={t('functions.thin_las.thinned_output_las')}
              label={t('functions.thin_las.thinned_output_las_description')}
              filter={lasFileFilter}
              value={outputPath}
              onChange={setOutputPath}
              saveFile
            />
          </Grid>
        </Grid>

        {inValidThinningValue && <FormHelperText className={classes.invalidThinningError} error>
          {t('functions.thin_las.invalid_thinning_value')}
        </FormHelperText>}

      </SimpleDialog>
    </React.Fragment>
  );
});


